// src/studio/pages/StudioHome.jsx — /studio
//
// The studio's project list, replacing rds_ui's RequirementStudio.jsx /
// DesignStudio.jsx split (one project, one pipeline — there is no longer a
// separate "design" landing page to keep in sync with this one).
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FolderKanban, Trash2, ArrowRight } from "lucide-react";
import {
  PageHeader,
  Button,
  DataTable,
  EmptyState,
  ConfirmModal,
} from "../../shared/ui";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useAuth } from "../../context/AuthContext";
import { studioProjectsApi } from "../api/studioApi";
import NewProjectModal from "../components/NewProjectModal";

export default function StudioHome() {
  const { currentEngagementId, currentEngagement, currentAccount } = useWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    studioProjectsApi
      .list(currentEngagementId)
      .then((data) => { if (!cancelled) setProjects(data); })
      .catch(() => { if (!cancelled) setError("Failed to load projects."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentEngagementId]);

  async function handleCreate(formData) {
    setCreating(true);
    try {
      const project = await studioProjectsApi.create({
        ...formData,
        engagement_id: currentEngagementId,
      });
      setProjects((prev) => [project, ...prev]);
      setShowCreate(false);
      navigate(`/studio/${project.id}/sources`);
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(project) {
    await studioProjectsApi.remove(project.id);
    setProjects((prev) => prev.filter((p) => p.id !== project.id));
    setToDelete(null);
  }

  const table = useMemo(
    () => ({
      columns: [
        {
          header: "Project",
          accessorKey: "name",
          cell: ({ row }) => (
            <div>
              <p className="text-sm font-medium text-slate-800">{row.original.name}</p>
              {row.original.client_name && (
                <p className="text-xs text-slate-400">{row.original.client_name}</p>
              )}
            </div>
          ),
        },
        {
          header: "Type",
          accessorKey: "project_type",
          cell: ({ getValue }) => getValue()
            ? <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">{getValue()}</span>
            : <span className="text-xs text-slate-300">—</span>,
        },
        {
          header: "Methodology",
          accessorKey: "methodology",
          cell: ({ getValue }) => <span className="text-sm text-slate-600">{getValue() || "—"}</span>,
        },
        {
          header: "Contract Type",
          accessorKey: "contract_type",
          cell: ({ getValue }) => getValue()
            ? <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">{getValue()}</span>
            : <span className="text-xs text-slate-300">—</span>,
        },
        {
          header: "Created",
          accessorKey: "created_at",
          cell: ({ getValue }) => {
            const value = getValue();
            if (!value) return <span className="text-xs text-slate-300">—</span>;
            const date = new Date(value.endsWith("Z") ? value : `${value}Z`);
            return (
              <span className="text-sm text-slate-600">
                {date.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })}
              </span>
            );
          },
        },
        {
          header: "Created by",
          accessorKey: "created_by",
          cell: ({ getValue }) => {
            const createdBy = getValue();
            if (createdBy == null) return <span className="text-xs text-slate-300">—</span>;
            // No name/email alongside the id on this response (unlike
            // engagements' account_manager_id, resolved from an embedded
            // team-members list) -- fall back to the raw id, same as
            // accounts/pages/ProjectsPage.jsx does for the same reason.
            const label = Number(user?.id) === Number(createdBy) ? "You" : String(createdBy);
            return <span className="text-sm text-slate-600">{label}</span>;
          },
        },
        {
          header: "",
          id: "actions",
          align: "right",
          sortAllowed: false,
          cell: ({ row }) => (
            <div className="flex items-center justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => navigate(`/studio/${row.original.id}/sources`)}>
                Open <ArrowRight size={12} />
              </Button>
              <button
                type="button"
                onClick={() => setToDelete(row.original)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                aria-label={`Delete ${row.original.name}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ),
        },
      ],
      rows: projects,
    }),
    [projects, navigate, user]
  );

  return (
    <div>
      <PageHeader
        title="Requirements & Design Studio"
        subtitle="Turn a SOW and the conversation around it into Jade's controlled requirements and design documents."
        actions={
          <Button onClick={() => setShowCreate(true)} disabled={!currentEngagementId}>
            <Plus size={14} /> New Draft
          </Button>
        }
      />

      {!currentEngagementId ? (
        <EmptyState
          icon={FolderKanban}
          title="Select a project in the top bar"
          description="Every studio project belongs to one engagement — pick it up there first."
        />
      ) : error ? (
        <EmptyState icon={FolderKanban} title="Couldn't load projects" description={error} />
      ) : !loading && projects.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="No projects yet"
          description="Create the first one for this engagement to get started."
          action={<Button onClick={() => setShowCreate(true)}><Plus size={14} /> New Draft</Button>}
        />
      ) : (
        <DataTable table={table} loading={loading} canSearch entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }} />
      )}

      <NewProjectModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreate}
        loading={creating}
        engagement={currentEngagement}
        accountName={currentAccount?.name}
        existingProjectsCount={projects.length}
      />

      <ConfirmModal
        open={Boolean(toDelete)}
        entityLabel="project"
        name={toDelete?.name}
        confirmLabel="Delete"
        onClose={() => setToDelete(null)}
        onConfirm={() => handleDelete(toDelete)}
      >
        This removes the project and everything generated for it. This cannot be undone.
      </ConfirmModal>
    </div>
  );
}
